// src/utils/validators.js

/**
 * Memeriksa apakah string kosong atau hanya berisi spasi.
 * @param {string} value - Nilai yang akan diperiksa.
 * @returns {boolean} - True jika kosong, false jika tidak.
 */
export const isEmpty = (value) => {
    return !value || value.trim() === '';
};

/**
 * Memvalidasi Nomor Induk Kependudukan (NIK) Indonesia.
 * Validasi NIK mencakup:
 * - Harus 16 digit
 * - Harus berupa angka
 * - 6 digit pertama mengandung kode provinsi, kabupaten/kota, dan kecamatan
 * - 6 digit berikutnya mengandung tanggal lahir dalam format DDMMYY
 * @param {string} nik - NIK yang akan divalidasi.
 * @returns {string|null} - Pesan error jika tidak valid, null jika valid.
 */
export const validateNIK = (nik) => {
    if (isEmpty(nik)) {
        return 'NIK wajib diisi.';
    }
    
    // Periksa panjang dan digit
    if (!/^\d{16}$/.test(nik)) {
        return 'NIK harus terdiri dari 16 digit angka.';
    }

    // Ekstrak tanggal lahir (digit 7-12)
    const birthDateCode = nik.substring(6, 12);
    // Ekstrak tanggal (untuk perempuan, tanggal + 40)
    let day = parseInt(birthDateCode.substring(0, 2), 10);
    const month = parseInt(birthDateCode.substring(2, 4), 10);
    let year = parseInt(birthDateCode.substring(4, 6), 10);
    
    // Koreksi untuk perempuan (tanggal > 40)
    const isFemale = day > 40;
    if (isFemale) {
        day -= 40;
    }

    // Validasi tanggal lahir
    if (day < 1 || day > 31 || month < 1 || month > 12) {
        return 'Kode tanggal lahir pada NIK tidak valid.';
    }

    // Validasi bulan memiliki jumlah hari yang tepat
    const currentYear = new Date().getFullYear();
    // Asumsi tahun 2000+ jika < 30, otherwise 1900+
    year = year < 30 ? 2000 + year : 1900 + year;
    
    const lastDayInMonth = new Date(year, month, 0).getDate();
    if (day > lastDayInMonth) {
        return 'Kode tanggal lahir pada NIK tidak valid.';
    }

    return null; // Valid
};

/**
 * Memvalidasi format email.
 * - Memastikan format email standard
 * - Memeriksa domain email
 * @param {string} email - Email yang akan divalidasi.
 * @returns {string|null} - Pesan error jika tidak valid, null jika valid.
 */
export const validateEmail = (email) => {
    if (isEmpty(email)) {
        return null; // Email opsional, jadi valid jika kosong
    }
    
    // Regex yang lebih ketat untuk validasi email
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
        return 'Format email tidak valid.';
    }

    // Validasi domain email
    const domain = email.split('@')[1];
    // Cek TLD (Top Level Domain)
    const tld = domain.split('.').pop();
    if (tld.length < 2) {
        return 'Domain email tidak valid.';
    }

    return null; // Valid
};

/**
 * Memvalidasi nomor telepon Indonesia.
 * Format yang valid:
 * - Diawali dengan 08xx, +628xx, 628xx, atau +62 8xx
 * - Minimal 10 digit, maksimal 13 digit (termasuk kode negara)
 * @param {string} phoneNumber - Nomor telepon yang akan divalidasi.
 * @returns {string|null} - Pesan error jika tidak valid, null jika valid.
 */
export const validatePhoneNumber = (phoneNumber) => {
    if (isEmpty(phoneNumber)) {
        return null; // Nomor telepon opsional
    }
    
    // Hapus semua karakter non-digit
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    
    // Validasi panjang umum
    if (digitsOnly.length < 10 || digitsOnly.length > 13) {
        return 'Nomor telepon harus memiliki 10-13 digit.';
    }
    
    // Validasi format Indonesia yang umum
    let isValidFormat = false;
    
    // Cek format awalan 08xxx, +628xxx, atau 628xxx
    if (
        phoneNumber.startsWith('08') || 
        phoneNumber.startsWith('+628') ||
        phoneNumber.startsWith('628') ||
        phoneNumber.match(/^\+62\s?8/)
    ) {
        isValidFormat = true;
    }
    
    if (!isValidFormat) {
        return 'Format nomor telepon Indonesia tidak valid. Gunakan format: 08xx, +628xx, atau 628xx.';
    }
    
    return null; // Valid
};

/**
 * Memvalidasi bahwa field tidak kosong.
 * @param {string} value - Nilai field.
 * @param {string} fieldName - Nama field untuk pesan error.
 * @returns {string|null} - Pesan error jika kosong, null jika tidak.
 */
export const validateRequired = (value, fieldName) => {
    if (isEmpty(value)) {
        return `${fieldName} wajib diisi.`;
    }
    return null;
};

/**
 * Memvalidasi tanggal lahir.
 * - Memastikan tanggal tidak di masa depan
 * - Opsional: memastikan usia masuk akal (misalnya, tidak lebih dari 120 tahun)
 * @param {string} dateString - Tanggal lahir dalam format YYYY-MM-DD.
 * @returns {string|null} - Pesan error jika tidak valid, null jika valid.
 */
export const validateBirthDate = (dateString) => {
    if (isEmpty(dateString)) {
        return null; // Anggap opsional, kecuali dipanggil setelah validateRequired
    }
    
    const birthDate = new Date(dateString);
    const today = new Date();
    
    // Validasi format tanggal valid
    if (isNaN(birthDate.getTime())) {
        return 'Format tanggal tidak valid.';
    }
    
    // Validasi tanggal tidak di masa depan
    if (birthDate > today) {
        return 'Tanggal lahir tidak boleh di masa depan.';
    }
    
    // Validasi usia masuk akal (tidak lebih dari 120 tahun)
    const maxAge = 120;
    const minDate = new Date();
    minDate.setFullYear(today.getFullYear() - maxAge);
    
    if (birthDate < minDate) {
        return `Usia tidak boleh lebih dari ${maxAge} tahun.`;
    }
    
    return null; // Valid
};

/**
 * Memvalidasi panjang teks.
 * @param {string} value - Nilai yang akan divalidasi.
 * @param {number} min - Panjang minimum.
 * @param {number} max - Panjang maksimum.
 * @param {string} fieldName - Nama field untuk pesan error.
 * @returns {string|null} - Pesan error jika tidak valid, null jika valid.
 */
export const validateLength = (value, min, max, fieldName) => {
    if (isEmpty(value)) {
        return null; // Anggap opsional, kecuali dipanggil setelah validateRequired
    }
    
    if (value.length < min) {
        return `${fieldName} minimal terdiri dari ${min} karakter.`;
    }
    
    if (value.length > max) {
        return `${fieldName} maksimal terdiri dari ${max} karakter.`;
    }
    
    return null; // Valid
};

/**
 * Memvalidasi nama dengan aturan khusus.
 * - Nama tidak boleh berisi angka atau karakter khusus
 * - Nama harus memiliki panjang yang masuk akal
 * @param {string} name - Nama yang akan divalidasi.
 * @returns {string|null} - Pesan error jika tidak valid, null jika valid.
 */
export const validateName = (name) => {
    if (isEmpty(name)) {
        return 'Nama wajib diisi.';
    }
    
    // Nama tidak boleh mengandung angka atau karakter khusus
    if (!/^[a-zA-Z\s.']+$/.test(name)) {
        return 'Nama tidak boleh mengandung angka atau karakter khusus.';
    }
    
    // Validasi panjang nama
    if (name.length < 2) {
        return 'Nama terlalu pendek.';
    }
    
    if (name.length > 100) {
        return 'Nama terlalu panjang.';
    }
    
    return null; // Valid
};

/**
 * Memvalidasi alamat.
 * @param {string} address - Alamat yang akan divalidasi.
 * @returns {string|null} - Pesan error jika tidak valid, null jika valid.
 */
export const validateAddress = (address) => {
    if (isEmpty(address)) {
        return null; // Anggap opsional, kecuali dipanggil setelah validateRequired
    }
    
    // Validasi panjang alamat
    if (address.length < 10) {
        return 'Alamat terlalu singkat, mohon masukkan alamat lengkap.';
    }
    
    if (address.length > 200) {
        return 'Alamat terlalu panjang.';
    }
    
    return null; // Valid
};

/**
 * Memvalidasi nomor paspor.
 * Format paspor Indonesia: 1 huruf diikuti 8 digit angka (contoh: A12345678)
 * @param {string} passport - Nomor paspor yang akan divalidasi.
 * @returns {string|null} - Pesan error jika tidak valid, null jika valid.
 */
export const validatePassport = (passport) => {
    if (isEmpty(passport)) {
        return null; // Anggap opsional, kecuali dipanggil setelah validateRequired
    }
    
    // Format paspor Indonesia: 1 huruf diikuti 8 digit angka
    if (!/^[A-Z][0-9]{8}$/.test(passport)) {
        return 'Format nomor paspor tidak valid. Format yang benar: 1 huruf diikuti 8 digit angka (contoh: A12345678).';
    }
    
    return null; // Valid
};